import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

import { Modal, Upload, Form, Tooltip, Progress, Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';

import FilterCheckboxes from '../../components/inputWrapper/filterBox';
import CustomTagInput from '../inputWrapper/customTagInput';
import { useAuth } from '../../routes/authContext';
import CustomButton from '../../components/button';
import FormInput from '../inputWrapper/formInput';

import {
  setVideoUploading,
  setAudioUploading,
  setAudioUploadError,
  setVideoUploadError,
  resetVideoUploadState,
  resetAudioUploadState,
  resetDocumentUploadState
} from '../../redux/slices/modalSlice';

import {
  handleSubmit,
  handleUpload,
  handleVideoSubmit,
  handleDocumentUpload,
  handleVisibilityChange
} from '../../helpers/uploadHelper';

import {
  useDeleteAudioByIdMutation,
  useDeleteVideoByIdMutation,
  useDeleteDocumentByIdMutation
} from '../../redux/slices/mediaApi';

import { maxLength, requiredField, validateNoLeadingSpace } from '../../helpers/validationHelper';

import { BRImages } from '../../assets/imgs';
import '../../assets/scss/index.scss';

const { Dragger } = Upload;

const UploadVideoModal = ({ visible, onClose, loading, mediaType }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { openNotification } = useAuth();

  const isVideo = mediaType === 'video';

  const {
    mediaTypes,
    isVideoUploading,
    isAudioUploading,
    isVideoUploadComplete,
    isAudioUploadComplete,
    mediaVisibilityOptions
  } = useSelector((state) => ({
    isVideoUploading: state.modal.isVideoUploading,
    isAudioUploading: state.modal.isAudioUploading,
    isVideoUploadComplete: state.modal.isVideoUploadComplete,
    isAudioUploadComplete: state.modal.isAudioUploadComplete,
    mediaTypes: state.modal.mediaTypes,
    mediaVisibilityOptions: state.modal.mediaVisibilityOptions
  }));

  const [deleteVideoById] = useDeleteVideoByIdMutation();
  const [deleteAudioById] = useDeleteAudioByIdMutation();
  const [deleteDocumentById] = useDeleteDocumentByIdMutation();

  const isUploadComplete = isVideo ? isVideoUploadComplete : isAudioUploadComplete;
  const isUploading = isVideo ? isVideoUploading : isAudioUploading;

  const [fileList, setFileList] = useState([]);
  const [bulkData, setBulkData] = useState({});
  const [docFileListByMediaUid, setDocFileListByMediaUid] = useState({});

  const [tags, setTags] = useState([]);

  const [isCancelButtonDisabled, setIsCancelButtonDisabled] = useState(false);
  const [isSaveAndNextDisabled, setIsSaveAndNextDisabled] = useState(true);
  const [isUploadAllDisabled, setIsUploadAllDisabled] = useState(true);
  const [allSubmitLoading, setAllSubmitLoading] = useState(false);
  const [selectedMediaUid, setSelectedMediaUid] = useState(null);
  const [disabledItems, setDisabledItems] = React.useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [removingDocFiles, setRemovingDocFiles] = useState({});
  const [isSubmittedSingleVideo, setIsSubmittedSingleVideo] = useState(false);
  const [isSubmittedAllVideo, setIsSubmittedAllVideo] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const removingDocFilesRef = useRef({});
  const uploadCancelTokensRef = useRef({});
  const documentUploadCancelTokensRef = useRef({});

  const REQUIRED_FIELDS = [
    'name',
    'title',
    'author',
    'metaTags',
    'videoCategory',
    'visibility'
  ];

  const createVideoObject = (originalData) => ({
    id: originalData.id || '',
    author: originalData.author || '',
    title: originalData.title || '',
    description: originalData.description || 'description',
    thumbnailUrl: '',
    language: 'en',
    accessPermissions: ['view', 'download'],
    relatedDocuments: originalData.relatedDocuments
      ? originalData.relatedDocuments.map((doc) => doc.id)
      : [],
    videoCategory: originalData.videoCategory || [],
    metaTags: originalData.metaTags || [],
    visibility: originalData.visibility || [],
    theme: originalData.theme || ''
  });

  const removeMedia = useCallback(
    async (file) => {
      if (uploadCancelTokensRef.current[file.uid]) {
        uploadCancelTokensRef.current[file.uid].cancel('Upload canceled by user.');
        return true;
      }

      if (isUploadComplete && file.id) {
        try {
          if (isVideo) {
            await deleteVideoById(file.id).unwrap();
          } else {
            await deleteAudioById(file.id).unwrap();
          }
          openNotification({
            message: `${isVideo ? 'Video' : 'Audio'} deleted successfully.`,
            type: 'success',
            title: 'Success'
          });
          return true;
        } catch (error) {
          openNotification({
            message: `${isVideo ? 'Video' : 'Audio'} deletion failed.`,
            type: 'error',
            title: 'Error'
          });
          return false;
        }
      }
      return false;
    },
    [deleteVideoById, deleteAudioById, isUploadComplete, isVideo, openNotification]
  );

  const handleRemove = useCallback(
    async (file, actions) => {
      setIsDeleting(true);
      const removed = await removeMedia(file);
      if (removed) {
        setFileList((prev) => prev.filter((item) => item.uid !== file.uid));
        setBulkData((prev) => {
          const { [file.uid]: _, ...rest } = prev;
          return rest;
        });

        setSelectedMediaUid((prevUid) => {
          if (prevUid === file.uid) {
            const currentIndex = fileList.findIndex((f) => f.uid === file.uid);
            const nextFile = fileList[currentIndex + 1];
            if (nextFile && nextFile.id && nextFile.status === 'done') {
              setIsSelected(true);
              return nextFile.uid;
            }
            setIsSelected(false);
            return null;
          }
          return prevUid;
        });
      }
      setIsDeleting(false);
    },
    [removeMedia, fileList]
  );

  const uploadFile = useCallback(
    async (file, onSuccess, onError, onProgress) => {
      const cancelTokenSource = axios.CancelToken.source();
      uploadCancelTokensRef.current[file.uid] = cancelTokenSource;

      try {
        await handleUpload({
          file,
          onProgress: (progress) => onProgress(progress),
          onSuccess: (response, fileObj) => {
            onSuccess(response, fileObj);
          },
          onError: (error) => {
            console.error(`Upload error for ${file.name}:`, error);
            onError(error);
          },
          isVideo,
          dispatch,
          setMediaId: (id) => {
            setBulkData((prevData) => ({
              ...prevData,
              [file.uid]: {
                ...prevData[file.uid],
                id,
                isMetaDataComplete: false,
                name: file.name
              }
            }));
          },
          setDuration: null,
          openNotification,
          setAudioLink: null,
          setBulkData,
          cancelToken: cancelTokenSource.token
        });
      } catch (error) {
        if (axios.isCancel(error)) {
          console.warn(`Upload canceled for file: ${file.name}`);
        } else {
          console.error(`Upload failed for file: ${file.name}`, error);
        }
        onError(error);
      } finally {
        delete uploadCancelTokensRef.current[file.uid];
      }
    },
    [handleUpload, isVideo, dispatch, openNotification]
  );

  const beforeUpload = useCallback(
    (file) => {
      if (!isVideo && fileList?.length >= 1) {
        openNotification({
          message: 'You can only upload one audio at a time!',
          type: 'warning',
          title: 'Warning'
        });
        return Upload.LIST_IGNORE;
      }
      const validType = isVideo ? file.type === 'video/mp4' : file.type === 'audio/mpeg';
      if (!validType) {
        openNotification({
          message: `You can only upload ${isVideo ? 'MP4' : 'MP3'} files!`,
          type: 'warning',
          title: 'Warning'
        });
        return Upload.LIST_IGNORE;
      }

      if (isVideo) {
        dispatch(setVideoUploading(true));
        dispatch(setVideoUploadError(false));
      } else {
        dispatch(setAudioUploading(true));
        dispatch(setAudioUploadError(false));
      }
      return true;
    },
    [dispatch, fileList.length, isVideo, openNotification]
  );

  const onChange = useCallback((info) => {
    setFileList((prevList) => {
      const updatedList = [...prevList];
      info.fileList.forEach((file) => {
        const existingIndex = updatedList.findIndex((f) => f.uid === file.uid);
        if (existingIndex === -1) {
          updatedList.push(file);
        } else {
          updatedList[existingIndex] = {
            ...updatedList[existingIndex],
            ...file
          };
        }
      });
      return updatedList;
    });
  }, []);

  const onDrop = useCallback(
    (e) => {
      const droppedFiles = Array.from(e.dataTransfer.files);
      const invalidFiles = droppedFiles.filter((file) =>
        isVideo ? file.type !== 'video/mp4' : file.type !== 'audio/mpeg'
      );
      if (invalidFiles.length > 0) {
        openNotification({
          message: `Invalid file type detected in dropped files! Only ${isVideo ? 'MP4' : 'MP3'
            } files are allowed.`,
          type: 'warning',
          title: 'Warning'
        });
      }
    },
    [isVideo, openNotification]
  );

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizeUnits = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizeUnits[i];
  };

  const isDocumentUploading = useMemo(() => {
    if (!selectedMediaUid) return false;
    const docs = docFileListByMediaUid[selectedMediaUid] || [];
    return docs.some((file) => file.status !== 'done');
  }, [docFileListByMediaUid, selectedMediaUid]);

  const itemRender = useCallback(
    (originNode, file, newFileList, actions) => {
      const uploadPercent = file.percent || 0;
      const uploadComplete = file.status === 'done';
      const isSelectedItem = selectedMediaUid === file.uid;
      const fileSize = file.size ? formatBytes(file.size) : '';
      const isMetaDataComplete = bulkData[file.uid]?.isMetaDataComplete || false;

      return (
        <div
          key={file.uid}
          className={`upload-item pt-5 bg-white radius-12 cursor-pointer ${isSelectedItem ? 'upload-item-selected' : ''
            }`}
          onClick={() => {
            if (isDocumentUploading) {
              openNotification({
                message:
                  'Document upload is in progress. Please wait until it completes before switching files.',
                type: 'warning',
                title: 'Please Wait'
              });
              return;
            }
            if (uploadComplete) {
              setSelectedMediaUid(file.uid);
              setIsSelected(true);
            }
          }}
          role='button'
          tabIndex={uploadComplete ? 0 : -1}
          aria-disabled={!uploadComplete}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && uploadComplete && !isDocumentUploading) {
              setSelectedMediaUid(file.uid);
              setIsSelected(true);
            }
          }}
        >
          <div className='flex justify-between w-100 items-center'>
            <div className='flex items-center'>
              <img
                src={
                  isVideo
                    ? BRImages.MediaPlayerImages.UploadListVideo
                    : BRImages.MediaPlayerImages.UploadListAudio
                }
                alt={`${isVideo ? 'video' : 'audio'}-upload-icon`}
              />
              <div className='flex flex-col'>
                <Tooltip title={file.name}>
                  <span className='text-ellipsis file-name'>{file.name}</span>
                </Tooltip>
                <span className='manrope-12-regular file-size'>{fileSize}</span>
              </div>
            </div>
            <div className='flex gap-20'>
              {file.status === 'done' ? (
                isMetaDataComplete ? (
                  <Tooltip
                    title={`${isVideo ? 'Video' : 'Audio'} uploaded. Required metadata is filled.`}
                  >
                    <img
                      src={BRImages.MediaPlayerImages.UploadDoneIcon}
                      alt='upload-status-icon'
                      className='cursor-default'
                    />
                  </Tooltip>
                ) : file.status === 'error' ? (
                  <Tooltip title={`Please retry the upload.`}>
                    <img
                      src={BRImages.MediaPlayerImages.DeleteIcon}
                      alt='upload-status-icon'
                      className='cursor-default'
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title={`Please complete the metadata before proceeding.`}>
                    <img
                      src={BRImages.MediaPlayerImages.MetaDataPendingIcon}
                      alt='upload-status-icon'
                      className='cursor-default'
                    />
                  </Tooltip>
                )
              ) : (
                <Tooltip title={`${isVideo ? 'Video' : 'Audio'} uploading.`}>
                  <img
                    src={BRImages.MediaPlayerImages.VideoUploading}
                    alt='upload-status-icon'
                    className='cursor-default'
                  />
                </Tooltip>
              )}
              {!isDeleting && (
                <div className='flex gap-24'>
                  <img
                    src={BRImages.CommonImages.CloseCircleIcon}
                    alt='close-icon'
                    onClick={(e) => {
                      e.preventDefault();
                      handleRemove(file, actions);
                    }}
                    className='close-icon'
                  />
                </div>
              )}
            </div>
          </div>
          {file.status !== 'done' && (
            <Progress
              percent={uploadPercent}
              size='small'
              status={uploadComplete ? 'success' : 'active'}
              className='w-100'
            />
          )}
        </div>
      );
    },
    [isDeleting, handleRemove, isVideo, selectedMediaUid, bulkData, isDocumentUploading]
  );

  const Dragprops = useMemo(
    () => ({
      name: 'file',
      multiple: true,
      customRequest: ({ file, onSuccess, onError, onProgress }) =>
        uploadFile(file, onSuccess, onError, onProgress),
      beforeUpload,
      onChange,
      onDrop,
      listType: 'picture',
      accept: isVideo ? '.mp4' : '.mp3',
      itemRender
    }),
    [isVideo, uploadFile, beforeUpload, onChange, onDrop, itemRender]
  );

  /**
   * Documents Upload config
   *    => Replaces the old "mapping from bulkData" with a "fully controlled" approach
   *       via docFileListByMediaUid.
   */
  const DocumentDragProps = useMemo(() => {
    return {
      name: 'file',
      multiple: true,
      accept: '.pdf,.doc,.docx,.ppt,.pptx',
      showUploadList: {
        showPreviewIcon: true,
        showRemoveIcon: true,
        removeIcon: (file) => {
          const isUploading = file.status !== 'done';
          return (
            <img
              src={BRImages.CommonImages.CloseCircleIcon}
              alt='close-icon'
              style={{
                cursor: isUploading ? 'not-allowed' : 'pointer',
                opacity: isUploading ? 0.2 : 1
              }}
              onClick={(e) => {
                if (!isUploading) {
                  e.stopPropagation();
                  e.preventDefault();
                  return;
                }
              }}
            />
          );
        }
      },
      beforeUpload: (file) => {
        const validTypes = ['.pdf', '.doc', '.docx', '.ppt', '.pptx'];
        const isValidType = validTypes.some((type) => file.name.endsWith(type));
        if (!isValidType) {
          openNotification({
            message: 'You can only upload PDF, DOC, DOCX, PPT, or PPTX files!',
            type: 'warning',
            title: 'Warning'
          });
          return Upload.LIST_IGNORE;
        }
        return true;
      },
      customRequest: ({ file, onSuccess, onError, onProgress }) => {
        const cancelTokenSource = axios.CancelToken.source();
        documentUploadCancelTokensRef.current[file.uid] = cancelTokenSource;
        file.mediaUid = selectedMediaUid;

        handleDocumentUpload({
          file,
          onProgress,
          onSuccess,
          onError,
          openNotification,
          dispatch,
          isVideo,
          selectedMediaUid,
          setBulkData,
          cancelToken: cancelTokenSource.token
        });
      },

      /**
       * onChange => Where we update docFileListByMediaUid so the <Upload> is fully controlled
       */
      onChange: (info) => {
        const { file, fileList: updatedList } = info;
        const mediaUid = file.mediaUid || selectedMediaUid;
        setDocFileListByMediaUid((prev) => ({
          ...prev,
          [mediaUid]: updatedList
        }));
      },

      onRemove: async (file) => {
        if (removingDocFilesRef.current[file.uid]) {
          return false;
        }

        removingDocFilesRef.current[file.uid] = true;

        if (file.status !== 'done') {
          removingDocFilesRef.current[file.uid] = false;
          return false;
        }

        const cancelTokenSource = documentUploadCancelTokensRef.current[file.uid];
        if (cancelTokenSource) {
          cancelTokenSource.cancel('Upload canceled by user.');
          delete documentUploadCancelTokensRef.current[file.uid];
        }

        setBulkData((prevData) => {
          const mediaData = prevData[selectedMediaUid] || {};
          return {
            ...prevData,
            [selectedMediaUid]: {
              ...mediaData,
              relatedDocuments: (mediaData.relatedDocuments || []).filter(
                (doc) => doc.uid !== file.uid
              )
            }
          };
        });

        try {
          const currentBulkData = { ...bulkData };
          if (file.uid) {
            for (const key of Object.keys(currentBulkData)) {
              if (!key.startsWith('rc-upload')) continue;
              if (!Array.isArray(currentBulkData[key].relatedDocuments)) continue;
              const relatedDocs = currentBulkData[key].relatedDocuments;
              for (let i = relatedDocs.length - 1; i >= 0; i--) {
                const doc = relatedDocs[i];
                if (doc.fileUUID === file.uid) {
                  const response = await deleteDocumentById(doc.id).unwrap();
                  if (response?.success === true) {
                    relatedDocs.splice(i, 1);
                    openNotification({
                      message: `${file.name} deleted successfully.`,
                      type: 'info',
                      title: 'Information'
                    });
                  }
                }
              }
            }
          }
          setBulkData({ ...currentBulkData });
          return true;
        } catch (error) {
          openNotification({
            message: 'Failed to delete document.',
            type: 'error',
            title: 'Error'
          });
          return false;
        } finally {
          delete removingDocFilesRef.current[file.uid];
        }
      }
    };
  }, [dispatch, isVideo, openNotification, selectedMediaUid, deleteDocumentById, bulkData]);

  /**
   * Reset the entire modal state. Called after finishing or closing.
   */
  const resetModalState = useCallback(async () => {
    // Cancel any ongoing uploads
    Object.values(uploadCancelTokensRef.current).forEach((cancelSource) => {
      cancelSource.cancel('Upload canceled by user.');
    });
    uploadCancelTokensRef.current = {};

    Object.values(documentUploadCancelTokensRef.current).forEach((cancelSource) => {
      cancelSource.cancel('Upload canceled by user.');
    });
    documentUploadCancelTokensRef.current = {};

    setTags([]);
    setFileList([]);
    setBulkData({});
    setDocFileListByMediaUid({});
    setDisabledItems([]);

    setIsSelected(false);
    setIsDeleting(false);
    setSubmitLoading(false);
    setAllSubmitLoading(false);
    setIsSaveAndNextDisabled(true);

    setSelectedMediaUid(null);

    form.resetFields();

    if (isVideo) {
      dispatch(resetVideoUploadState());
    } else {
      dispatch(resetAudioUploadState());
    }
    dispatch(resetDocumentUploadState());
    onClose();
  }, [dispatch, form, isVideo, onClose]);

  /**
   * Cancel the entire upload session, removing all uploads from server (if they were done).
   */
  const cancelUpload = useCallback(async () => {
    // Cancel any ongoing main media uploads
    Object.values(uploadCancelTokensRef.current).forEach((cancelSource) => {
      cancelSource.cancel('Upload canceled by user.');
    });
    uploadCancelTokensRef.current = {};

    // Cancel any ongoing doc uploads
    Object.values(documentUploadCancelTokensRef.current).forEach((cancelSource) => {
      cancelSource.cancel('Upload canceled by user.');
    });
    documentUploadCancelTokensRef.current = {};

    // Try to delete any partially uploaded files from server
    try {
      const deletePromises = fileList.map(async (file) => {
        if (file.id) {
          if (isVideo) {
            await deleteVideoById(file.id).unwrap();
          } else {
            await deleteAudioById(file.id).unwrap();
          }
        }
      });
      await Promise.all(deletePromises);
    } catch (error) {
      openNotification({
        message: 'Error while deleting uploaded files. Please try again later.',
        type: 'error',
        title: 'Error'
      });
    }

    setFileList([]);
    setBulkData({});
    setDocFileListByMediaUid({});
    setTags([]);
    setSelectedMediaUid(null);
    setIsSelected(false);
    setSubmitLoading(false);
    setAllSubmitLoading(false);
    setIsDeleting(false);
    setIsSaveAndNextDisabled(true);
    form.resetFields();

    if (isVideo) {
      dispatch(resetVideoUploadState());
    } else {
      dispatch(resetAudioUploadState());
    }
    dispatch(resetDocumentUploadState());
  }, [deleteAudioById, deleteVideoById, dispatch, fileList, form, isVideo, openNotification]);

  /**
   * Confirm whether user wants to close (losing unsaved changes)
   */
  const handleCancel = useCallback(() => {
    if (Object.keys(fileList).length > 0) {
      Modal.confirm({
        title: 'Confirm Close',
        content: 'You have unsaved changes. Are you sure you want to close?',
        okText: 'Yes',
        cancelText: 'No',
        onOk: async () => {
          await cancelUpload();
          onClose();
        },
        onCancel: () => {
          // Do nothing, keeping the main modal open
        }
      });
      return;
    }

    cancelUpload().then(onClose);
  }, [fileList, cancelUpload, onClose]);

  /**
   * Validate whether required fields are filled for each item
   */
  const validateBulkData = (data) => {
    const validationResults = {};
    Object.keys(data).forEach((key) => {
      if (!key.startsWith('rc-upload')) return;
      const item = data[key];

      let isComplete = true;
      REQUIRED_FIELDS.forEach((field) => {
        const value = item[field];
        if (
          value == null ||
          (typeof value === 'string' && value.trim() === '') ||
          (Array.isArray(value) && value.length === 0)
        ) {
          isComplete = false;
        }
      });

      validationResults[key] = isComplete;
    });
    return validationResults;
  };

  /**
   * Update a single field in bulkData for the current media.
   */
  const handleInputChange = useCallback(
    (field, value) => {
      if (!selectedMediaUid) return;
      setBulkData((prev) => {
        const updatedData = {
          ...prev,
          [selectedMediaUid]: {
            ...prev[selectedMediaUid],
            [field]: value
          }
        };

        const validation = validateBulkData(updatedData);
        const mediaIds = Object.keys(updatedData).filter((key) => key.startsWith('rc-upload'));
        mediaIds.forEach((id) => {
          updatedData[id].isMetaDataComplete = validation[id];
        });

        return updatedData;
      });
    },
    [selectedMediaUid]
  );

  /**
   * Called when user checks/unchecks the "Video Type" or "Audio Type" checkboxes
   */
  const handleMediaTypeChange = useCallback(
    (selectedValues) => {
      if (isVideo) {
        handleInputChange('videoCategory', selectedValues);
      } else {
        handleInputChange('audioTypeIDs', selectedValues);
      }
      const currentVisibility = form.getFieldValue('visibility') || [];
      const { effectiveValues, newDisabledItems } = handleVisibilityChange(
        currentVisibility,
        selectedValues
      );

      form.setFieldsValue({ visibility: effectiveValues });
      handleInputChange('visibility', effectiveValues);
      setDisabledItems(newDisabledItems);
    },
    [form, handleInputChange, isVideo]
  );

  /**
   * Called when user checks/unchecks "Visibility" checkboxes
   */
  const handleMediaVisibilityChange = useCallback(
    (selectedValues) => {
      const mediaType = form.getFieldValue('mediaType');
      const { effectiveValues, newDisabledItems } = handleVisibilityChange(
        selectedValues,
        mediaType
      );

      form.setFieldsValue({ visibility: effectiveValues });
      handleInputChange('visibility', effectiveValues);
      setDisabledItems(newDisabledItems);
    },
    [form, handleInputChange]
  );

  /**
   * "Save & Next" button to proceed to the next file
   */
  const saveAndNext = useCallback(() => {
    if (!selectedMediaUid) return;

    const currentIndex = fileList.findIndex((file) => file.uid === selectedMediaUid);
    if (currentIndex === -1) return;
    const nextFile = fileList[currentIndex + 1];

    if (nextFile && nextFile.id && nextFile.status === 'done') {
      setSelectedMediaUid(nextFile.uid);
      setIsSelected(true);
    } else if (nextFile && nextFile.status !== 'done') {
      openNotification({
        message: `Please wait until "${nextFile.name}" is uploaded before proceeding.`,
        type: 'warning',
        title: 'Warning'
      });
    } else {
      openNotification({
        message: 'No more media items to process.',
        type: 'info',
        title: 'Information'
      });
    }
  }, [fileList, selectedMediaUid, openNotification]);

  /**
   * Submits metadata for just the current (selected) video, then removes it from the lists
   */
  const handleSingleVideoSubmit = useCallback(async () => {
    setIsSubmittedSingleVideo(true);
    setIsCancelButtonDisabled(true);
    if (!selectedMediaUid) return;

    const selectedData = bulkData[selectedMediaUid];
    setSubmitLoading(true);
    try {
      const newVideoObject = createVideoObject(selectedData);
      await handleVideoSubmit(
        newVideoObject,
        openNotification,
        isVideo,
        handleInputChange,
        setBulkData
      );

      // Remove from main file list
      setFileList((prev) => prev.filter((f) => f.uid !== selectedMediaUid));
      setBulkData((prev) => {
        const { [selectedMediaUid]: _, ...rest } = prev;
        return rest;
      });
      setDocFileListByMediaUid((prev) => {
        const { [selectedMediaUid]: _, ...rest } = prev;
        return rest;
      });

      // If that was the only file, reset
      if (Object.keys(bulkData).length === 1) {
        await resetModalState();
      } else {
        // Otherwise, move on to the next file if it exists
        const remainingFiles = fileList.filter((f) => f.uid !== selectedMediaUid);
        if (remainingFiles.length > 0) {
          const nextFile = remainingFiles[0];
          setSelectedMediaUid(nextFile.uid);
          setIsSelected(true);
        } else {
          setSelectedMediaUid(null);
          setIsSelected(false);
          form.resetFields();
        }
      }
    } catch (error) {
      openNotification({
        message: `Failed to save ${isVideo ? 'Video' : 'Audio'} metadata. Please try again.`,
        type: 'error',
        title: 'Error'
      });
    } finally {
      setSubmitLoading(false);
      setIsCancelButtonDisabled(false);
      setIsSubmittedSingleVideo(false);
    }
  }, [
    selectedMediaUid,
    bulkData,
    handleVideoSubmit,
    openNotification,
    isVideo,
    handleInputChange,
    setBulkData,
    fileList,
    form,
    resetModalState
  ]);

  /**
   * Submits *all* files' metadata in bulk
   */
  const handleAllVideoSubmit = useCallback(async () => {
    setIsSubmittedAllVideo(true);
    setAllSubmitLoading(true);
    setIsCancelButtonDisabled(true);

    try {
      const mediaEntries = Object.values(bulkData);

      for (const media of mediaEntries) {
        if (media.name && media.name.trim() !== '') {
          const newVideoObject = createVideoObject(media);
          await handleVideoSubmit(
            newVideoObject,
            openNotification,
            isVideo,
            handleInputChange,
            setBulkData
          );
        }
      }
      await resetModalState();
      onClose();
    } catch (error) {
      openNotification({
        message: `Failed to upload all ${isVideo ? 'videos' : 'audios'}. Please try again.`,
        type: 'error',
        title: 'Error'
      });
    } finally {
      setAllSubmitLoading(false);
      setIsCancelButtonDisabled(false);
      setIsSubmittedAllVideo(false);
    }
  }, [bulkData, handleInputChange, isVideo, openNotification, resetModalState, onClose]);

  const handleFieldsChange = useCallback(() => {
    form
      .validateFields()
      .then(() => {
        setIsSaveAndNextDisabled(false);
      })
      .catch(() => {
        setIsSaveAndNextDisabled(true);
      });
  }, [form]);

  /**
   * Whenever the user selects a file from the left list, load its metadata into the form
   */
  useEffect(() => {
    if (selectedMediaUid && bulkData[selectedMediaUid]) {
      const {
        title,
        author,
        theme,
        metaTags,
        videoCategory,
        visibility,
        relatedDocuments,
        description
      } = bulkData[selectedMediaUid];

      form.setFieldsValue({
        title,
        author,
        theme,
        metaTags,
        mediaType: videoCategory,
        visibility,
        description,
        documents:
          relatedDocuments?.map((doc) => ({
            uid: doc.uid,
            name: doc.name,
            status: 'done',
            sharableLink: doc.sharableLink,
            lastModifiedDate: doc.lastModifiedDate,
            url: doc.url
          })) || []
      });

      setTags(metaTags || []);
      handleFieldsChange();
    } else {
      form.resetFields();
      setTags([]);
      setIsSaveAndNextDisabled(true);
    }
  }, [selectedMediaUid, bulkData, form, handleFieldsChange]);

  useEffect(() => {
    if (selectedMediaUid && bulkData[selectedMediaUid]) {
      const videoTypeSelection = bulkData[selectedMediaUid].videoCategory || [];
      const currentVisibility = form.getFieldValue('visibility') || [];
      const { effectiveValues, newDisabledItems } = handleVisibilityChange(
        currentVisibility,
        videoTypeSelection
      );
      form.setFieldsValue({ visibility: effectiveValues });
      setDisabledItems(newDisabledItems);
    } else {
      setDisabledItems([]);
    }
  }, [selectedMediaUid, bulkData, form]);

  /**
   * Enable or disable "Upload All" depending on whether all items are complete
   */
  useEffect(() => {
    const rcUploadKeys = Object.keys(bulkData).filter((key) => key.startsWith('rc-upload'));
    if (rcUploadKeys.length === 0) {
      setIsUploadAllDisabled(true);
      return;
    }
    const allComplete = rcUploadKeys.every((key) => bulkData[key].isMetaDataComplete === true);
    setIsUploadAllDisabled(!allComplete || isUploading);
  }, [bulkData, isUploading]);

  /**
   * Render the footer buttons
   */
  const renderFooter = useCallback(() => {
    if (isVideo) {
      return isVideoUploading || isVideoUploadComplete ? (
        <div className='flex justify-end assign-modal-footer gap-12'>
          <CustomButton
            title='Cancel'
            variant='outline'
            className='radius-5'
            onClick={handleCancel}
            disabled={isCancelButtonDisabled}
          />
          <CustomButton
            title='Upload File'
            className='radius-5'
            onClick={handleSingleVideoSubmit}
            loading={submitLoading}
            disabled={
              isSaveAndNextDisabled ||
              allSubmitLoading ||
              isDocumentUploading ||
              isSubmittedSingleVideo ||
              isSubmittedAllVideo
            }
          />
          <CustomButton
            title='Upload All'
            className='radius-5'
            onClick={handleAllVideoSubmit}
            loading={allSubmitLoading}
            disabled={
              isSaveAndNextDisabled ||
              isUploadAllDisabled ||
              allSubmitLoading ||
              isDocumentUploading ||
              isSubmittedSingleVideo ||
              isSubmittedAllVideo
            }
          />
        </div>
      ) : (
        <div className='flex justify-end assign-modal-footer gap-12'>
          <CustomButton
            title='Cancel'
            variant='outline'
            className='radius-5'
            onClick={handleCancel}
            disabled={isCancelButtonDisabled}
          />
        </div>
      );
    }

    return (
      <div className='flex justify-end assign-modal-footer gap-12'>
        <CustomButton
          title='Cancel'
          variant='outline'
          className='radius-5'
          onClick={handleCancel}
        />
        <CustomButton
          title='Save'
          className='radius-5'
          onClick={handleSubmit}
          loading={loading || submitLoading}
          disabled={loading || isUploading || submitLoading || allSubmitLoading}
        />
      </div>
    );
  }, [
    isVideo,
    isVideoUploading,
    isVideoUploadComplete,
    handleCancel,
    handleSingleVideoSubmit,
    handleAllVideoSubmit,
    submitLoading,
    allSubmitLoading,
    isSaveAndNextDisabled,
    isUploadAllDisabled,
    loading,
    isUploading,
    isCancelButtonDisabled
  ]);

  const renderCloseIcon = useMemo(
    () => (
      <div className='flex justify-between items-center'>
        <img
          src={BRImages.CommonImages.CloseGrayIcon}
          alt='close-icon'
          className='close-icon p-10 radius-4'
          onClick={handleCancel}
        />
      </div>
    ),
    [onClose]
  );

  const handleNoLeadingSpaceChange = useCallback(
    (field) => {
      return (e) => {
        let { value } = e.target;
        if (value.startsWith(' ')) {
          value = value.trimStart();
        }
        handleInputChange(field, value);
      };
    },
    [handleInputChange]
  );

  const handlePaste = (e) => {
    const pastedText = e.clipboardData.getData('text').trimStart();
    // e.target.value = pastedText;
    e.target.dispatchEvent(new Event('input', { bubbles: true }));
  };

  return (
    <Modal
      visible={visible}
      title={`Upload ${isVideo ? 'Video' : 'Audio'}`}
      // onClose={handleCancel}
      destroyOnClose={false}
      maskClosable={false}
      footer={renderFooter()}
      closeIcon={renderCloseIcon}
      className={`upload-video-modal common-modal common-modal-container ${isVideo
        ? isVideoUploading || isVideoUploadComplete
          ? 'video-modal'
          : 'video-none-modal'
        : ''
        }`}
    >
      <Form
        form={form}
        layout='vertical'
        requiredMark={false}
        className={`upload-form-container ${isVideo ? (isVideoUploading || isVideoUploadComplete) && 'upload-video-form' : ''
          }`}
      >
        <div className={isVideo ? 'video-dragger' : ''}>
          <Form.Item className='mb-16'>
            <Dragger
              {...Dragprops}
              fileList={fileList}
              itemRender={itemRender}
              className={`upload-modal-dragger ${isVideo ? 'video-modal-dragger' : ''}`}
            >
              <img
                src={BRImages.MediaPlayerImages.UploadBtnIcon}
                alt='upload-icon'
                className='mb-8'
              />
              <p className='ant-upload-text m-0 mb-5 manrope-16-bold'>
                Drag & drop {mediaType} or{' '}
                <span className='browse-click cursor-pointer'>Browse</span>
              </p>
              <p className='ant-upload-hint m-0 manrope-14-regular'>
                Supported formats: {isVideo ? 'MP4' : 'MP3'}
              </p>
            </Dragger>
          </Form.Item>
        </div>

        {/* Right panel with metadata fields only appears if it's video OR we have selected a file */}
        {!isVideo || (selectedMediaUid && isSelected && bulkData[selectedMediaUid]) ? (
          <div
            className={
              isVideo
                ? isVideoUploading || isVideoUploadComplete
                  ? 'form-right-col'
                  : 'd-none'
                : ''
            }
          >
            <div className='selected-video-detail mb-20'>
              <Tooltip title={bulkData[selectedMediaUid]?.name}>
                <p className='selected-video-title manrope-18-semibold m-0 mb-10 truncate-text'>
                  {bulkData[selectedMediaUid]?.name}
                </p>
              </Tooltip>
            </div>

            <FormInput
              label='Title'
              name='title'
              placeholder='Enter title'
              rules={[requiredField(''), maxLength(100, 'Title cannot exceed 100 characters')]}
              maxLength={100}
              validateStatus={form.getFieldError('title').length ? 'error' : ''}
              onChange={handleNoLeadingSpaceChange('title')}
              onPaste={handlePaste}
            />
            <FormInput
              label='Speaker'
              name='author'
              placeholder='Enter speaker'
              rules={[requiredField(''), maxLength(75, 'Speaker cannot exceed 75 characters')]}
              maxLength={75}
              validateStatus={form.getFieldError('author').length ? 'error' : ''}
              onChange={handleNoLeadingSpaceChange('author')}
              onPaste={handlePaste}
            />

            {isVideo && (
              <>
                <FormInput
                  label='Theme'
                  name='theme'
                  placeholder='Enter theme'
                  rules={[maxLength(75, 'Theme cannot exceed 75 characters')]}
                  maxLength={75}
                  onChange={handleNoLeadingSpaceChange('theme')}
                  onPaste={handlePaste}
                />
                <Form.Item
                  name='metaTags'
                  label='Tags'
                  className='tags-section'
                  rules={[
                    {
                      validator: (_, tagsVal) => {
                        if (!tagsVal || tagsVal.length === 0) {
                          return Promise.reject(new Error('Please add at least one tag'));
                        }
                        if (tagsVal.some((tag) => tag.startsWith(' '))) {
                          return Promise.reject(new Error('Tags cannot start with a space'));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <CustomTagInput
                    value={bulkData[selectedMediaUid]?.metaTags}
                    onChange={(newTags) => handleInputChange('metaTags', newTags)}
                  />
                </Form.Item>
              </>
            )}

            <Form.Item
              name='mediaType'
              label={isVideo ? 'Video Type' : 'Audio Type'}
              rules={[{ required: true, message: 'Please select at least one option' }]}
            >
              <FilterCheckboxes filters={mediaTypes} onChange={handleMediaTypeChange} />
            </Form.Item>
            <Form.Item
              name='visibility'
              label='Visibility'
              className='visibility-filter'
              rules={[{ required: true, message: 'Please select at least one option' }]}
            >
              <FilterCheckboxes
                filters={mediaVisibilityOptions}
                value={bulkData[selectedMediaUid]?.visibility}
                disabledItems={disabledItems}
                onChange={handleMediaVisibilityChange}
              />
            </Form.Item>

            {/* DOCUMENTS Upload: now fully controlled by docFileListByMediaUid */}
            <Form.Item
              name='documents'
              getValueFromEvent={(e) => e?.fileList || []}
              className='upload-file-container w-100 text-nowrap'
            >
              <Upload
                {...DocumentDragProps}
                fileList={docFileListByMediaUid[selectedMediaUid] || []}
                className='upload-doc-btn'
              >
                <span className='upload-doc-label'>Upload Files</span>
                <Button
                  icon={<img src={BRImages.CommonImages.AddBlueIconCircled} alt='upload-icon' />}
                  className='border-none shadow-none pb-10'
                />
              </Upload>
            </Form.Item>

            {fileList.length > 1 && selectedMediaUid !== fileList[fileList.length - 1]?.uid && (
              <CustomButton
                title='Save & Next'
                variant='outline'
                className='radius-5 save-nxt-btn w-auto manrope-16-semibold'
                onClick={saveAndNext}
                disabled={isSaveAndNextDisabled || isDocumentUploading}
              />
            )}
          </div>
        ) : isVideoUploadComplete ? (
          !selectedMediaUid && !isSelected && !bulkData[selectedMediaUid] ? (
            <div className='text-center m-auto flex flex-col gap-32 p-48'>
              <img
                src={BRImages.EcclesialImages.SelectPDFIcon}
                alt='select-pdf-icon'
                className='select-icon'
              />
              <p className='m-0 manrope-16-regular empty-view-text'>
                Select uploaded file to show meta data
              </p>
            </div>
          ) : null
        ) : null}
      </Form>
    </Modal>
  );
};

UploadVideoModal.propTypes = {
  loading: PropTypes.bool,
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mediaType: PropTypes.oneOf(['video', 'audio']).isRequired
};

UploadVideoModal.defaultProps = {
  loading: false
};

export default React.memo(UploadVideoModal);
